import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { useLobby } from '@/hooks/useLobby';
import ChannelList from '@/components/lobby/ChannelList';
import MessageList from '@/components/lobby/MessageList';
import MessageInput from '@/components/lobby/MessageInput';
import UserList from '@/components/lobby/UserList';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { LobbyChannel } from '@/types/supabase';
import { useUserMessages } from '@/hooks/useUserMessages';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

const Lobby = () => {
  const { channelSlug = 'general' } = useParams<{ channelSlug?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isBlocked, setIsBlocked] = useState<boolean | null>(null);

  const {
    channels,
    activeChannel,
    messages,
    loadingChannels,
    loadingMessages,
    sendMessage,
    channelMembers,
    onlineUsers,
    loadMoreMessages,
    hasMoreMessages,
    loadingMoreMessages,
    toggleReaction,
    deleteMessage,
    editMessage,
    profile,
  } = useLobby(channelSlug);

  const { requestConnection, connections } = useUserMessages();
  const [userToConnect, setUserToConnect] = useState<{ id: string; name: string } | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const checkBlocked = async () => {
      if (!user) { setIsBlocked(null); return; }
      const { data, error } = await supabase.rpc('is_user_blocked', { p_section: 'lobby' });
      if (!mounted) return;
      if (error) {
        // default to not blocked on error; RLS still enforces
        setIsBlocked(false);
      } else {
        setIsBlocked(!!data);
      }
    };
    checkBlocked();
    return () => { mounted = false; };
  }, [user]);

  const handleChannelSelect = (channel: LobbyChannel) => {
    navigate(`/lobby/${channel.slug}`);
  };

  const handleInitiateConnect = (userId: string, userName: string) => {
    setUserToConnect({ id: userId, name: userName });
  };

  const handleConfirmConnect = async () => {
    if (!userToConnect) return;
    await requestConnection(userToConnect.id);
    setUserToConnect(null);
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p>Please log in to join the lobby.</p>
        </div>
      </MainLayout>
    );
  }

  if (isBlocked) {
    return (
      <MainLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Access Restricted</CardTitle>
              <CardDescription>Lobby access has been disabled for your account.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                If you believe this is a mistake, please contact support.
              </p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="h-full flex">
        <aside className="w-64 flex-shrink-0 border-r bg-background">
          <div className="h-full p-4 flex flex-col">
            <h2 className="text-xl font-bold mb-4">Channels</h2>
            <div className="flex-1">
              {loadingChannels ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : (
                <ChannelList
                  channels={channels}
                  activeChannel={activeChannel}
                  onSelectChannel={handleChannelSelect}
                />
              )}
            </div>
          </div>
        </aside>
        
        <main className="flex-1 flex flex-col h-full">
          <header className="p-4 border-b">
            <h1 className="text-2xl font-bold">{activeChannel?.name || <Skeleton className="h-8 w-48" />}</h1>
            <div className="text-muted-foreground">{activeChannel?.description || <Skeleton className="h-4 w-64 mt-1" />}</div>
          </header>
          <div className="flex-1 overflow-hidden">
            <MessageList
              messages={messages}
              loading={loadingMessages}
              onLoadMore={loadMoreMessages}
              hasMore={hasMoreMessages}
              loadingMore={loadingMoreMessages}
              onToggleReaction={toggleReaction}
              onDelete={deleteMessage}
              onEdit={editMessage}
              profile={profile}
              connections={connections}
              onConnect={handleInitiateConnect}
              channelMembers={channelMembers}
            />
          </div>
          <div className="p-4 border-t bg-background">
            <MessageInput 
              onSendMessage={sendMessage}
              channelName={activeChannel?.name || 'general'} 
              channelMembers={channelMembers}
              profile={profile}
            />
          </div>
        </main>

        {activeChannel?.show_user_list && (
          <aside className="w-64 flex-shrink-0 border-l bg-background overflow-hidden">
            <div className="h-full">
              <UserList members={channelMembers} onlineUsers={onlineUsers} />
            </div>
          </aside>
        )}
      </div>

      <Dialog open={!!userToConnect} onOpenChange={() => setUserToConnect(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Connection Request</DialogTitle>
            <DialogDescription>
              Would you like to send a connection request to {userToConnect?.name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUserToConnect(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmConnect}>
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Lobby;